'use client';

import React, { memo } from 'react';
import Image from 'next/image';

const IconTile = ({ children }) => (
  <span
    className="inline-flex h-14 w-14 items-center justify-center rounded-[14px] shrink-0"
    style={{
      background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
      boxShadow: '0 4px 20px rgba(201,168,76,0.18), 0 1px 4px rgba(0,0,0,0.18)',
      border: '1.5px solid rgba(201,168,76,0.45)',
    }}
    aria-hidden="true"
  >
    {children}
  </span>
);

const OutlineButton = ({ className = '', children, ...props }) => (
  <a
    {...props}
    className={[
      'inline-flex h-10 items-center justify-center rounded-[12px] border-[1.5px] border-slate-300 px-4',
      'text-[#0B1D3A] text-[15px] font-semibold leading-none',
      'transition-colors',
      'hover:border-slate-400 hover:bg-slate-50/60',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-800/60',
      'active:bg-slate-100',
      className,
    ].join(' ')}
  >
    {children}
  </a>
);

const Card = ({ children, bgDecoration, ariaLabelledby, className = '' }) => (
  <article
    aria-labelledby={ariaLabelledby}
    className={[
      'relative overflow-hidden rounded-[16px] border border-slate-200 bg-white',
      'shadow-[0_1px_2px_rgba(15,23,42,0.04)] px-6 sm:px-8 py-6 sm:py-8',
      className,
    ].join(' ')}
  >
    {bgDecoration ? (
      <div
        className="pointer-events-none absolute -bottom-6 -right-6 opacity-30 select-none"
        aria-hidden="true"
      >
        {bgDecoration}
      </div>
    ) : null}
    {children}
  </article>
);

const SectionHeading = ({ id, title }) => (
  <header className="mb-8 sm:mb-10 md:mb-12 text-center">
    <h2
      id={id}
      className="text-[#0B1D3A] font-extrabold tracking-[-0.01em] text-[clamp(28px,4vw,40px)] leading-[1.15]"
    >
      {title}
    </h2>
  </header>
);

function StandForSectionComponent() {
  return (
    <section
      aria-labelledby="standfor-heading"
      className="w-full  bg-[#F1F3F6] py-12 sm:py-14 md:py-16"
    >
      <div className="mx-auto w-full max-w-[1200px] px-4 sm:px-6 md:px-8">
        <SectionHeading id="standfor-heading" title="What We Stand For" />

        {/* Grid: stacked on mobile, 12-col from md+ */}
        <div className="grid grid-cols-1 gap-6 sm:gap-8 md:grid-cols-12">
          {/* Row 1: Mission card (8 cols) + image (4 cols) */}
          <Card
            ariaLabelledby="mission-title"
            className="md:col-span-8"
          >
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 flex items-center gap-3">
                <IconTile>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <defs>
                      <linearGradient id="mission-grad" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#C9A84C" />
                        <stop offset="100%" stopColor="#F5D080" />
                      </linearGradient>
                    </defs>
                    {/* Rocket body */}
                    <path
                      d="M12 2C12 2 7 6 7 13H17C17 6 12 2 12 2Z"
                      fill="url(#mission-grad)" opacity="0.25"
                      stroke="url(#mission-grad)" strokeWidth="1.4" strokeLinejoin="round"
                    />
                    {/* Rocket window */}
                    <circle cx="12" cy="10" r="1.8" fill="url(#mission-grad)" opacity="0.7" />
                    {/* Rocket fins */}
                    <path d="M7 13L4.5 16H7" stroke="url(#mission-grad)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M17 13L19.5 16H17" stroke="url(#mission-grad)" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                    {/* Nozzle */}
                    <path d="M9.5 13H14.5V15.5C14.5 15.5 13 17 12 17C11 17 9.5 15.5 9.5 15.5V13Z" fill="url(#mission-grad)" opacity="0.3" stroke="url(#mission-grad)" strokeWidth="1.3" strokeLinejoin="round" />
                    {/* Flame */}
                    <path d="M10.5 17C10.5 17 11 19.5 12 20C13 19.5 13.5 17 13.5 17" stroke="url(#mission-grad)" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                </IconTile>
                <h3
                  id="mission-title"
                  className="text-[#0B1D3A] font-semibold text-[clamp(18px,2.2vw,20px)] leading-[1.3]"
                >
                  Our Mission
                </h3>
              </div>

              <p className="col-span-12 mt-2 text-[#0B1D3A] text-[clamp(16px,2.1vw,16px)] leading-[clamp(22px,3.2vw,24px)] max-w-[70ch]">
                To manufacture application ready labels that support branding, packaging performance and production efficiency for businesses across multiple industries.
              </p>

              <div className="col-span-12 mt-5">
                <OutlineButton href="/portfolio" className='text-[#174873]'>Explore Our Work</OutlineButton>
              </div>
            </div>
          </Card>

          <figure className="relative overflow-hidden rounded-[16px] md:col-span-4">
            <div className="relative w-full aspect-[4/3]">
              <Image
                src="/images/standfor-mission.jpg"
                alt="Quality control worker inspecting printed label rolls on a flexographic press"
                fill
                className="object-cover"
                sizes="(min-width:1024px) 33vw, (min-width:768px) 50vw, 100vw"
                priority={false}
              />
            </div>
          </figure>

          {/* Row 2: image (4 cols) + Vision card (8 cols) */}
          <figure className="relative overflow-hidden rounded-[16px] md:col-span-4">
            <div className="relative w-full aspect-[4/3]">
              <Image
                src="/images/standfor-vision.jpg"
                alt="Technician operating a modern flexographic label printing machine"
                fill
                className="object-cover"
                sizes="(min-width:1024px) 33vw, (min-width:768px) 50vw, 100vw"
                priority={false}
              />
            </div>
          </figure>

          <Card
            ariaLabelledby="vision-title"
            className="md:col-span-8"
          >
            <div className="grid grid-cols-12 gap-4">
              <div className="col-span-12 flex items-center gap-3">
                <IconTile>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <defs>
                      <linearGradient id="vision-grad" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#C9A84C" />
                        <stop offset="100%" stopColor="#F5D080" />
                      </linearGradient>
                    </defs>
                    {/* Crown base */}
                    <path
                      d="M4 17H20L18 10L14 14L12 8L10 14L6 10L4 17Z"
                      fill="url(#vision-grad)" opacity="0.25"
                      stroke="url(#vision-grad)" strokeWidth="1.4" strokeLinejoin="round" strokeLinecap="round"
                    />
                    {/* Crown band */}
                    <rect x="4" y="17" width="16" height="2.5" rx="1" fill="url(#vision-grad)" opacity="0.5" />
                    {/* Crown jewel dots */}
                    <circle cx="12" cy="8.5" r="1.2" fill="url(#vision-grad)" />
                    <circle cx="6.2" cy="10.5" r="1" fill="url(#vision-grad)" opacity="0.8" />
                    <circle cx="17.8" cy="10.5" r="1" fill="url(#vision-grad)" opacity="0.8" />
                    {/* Radiant top rays */}
                    <path d="M12 2V4" stroke="url(#vision-grad)" strokeWidth="1.3" strokeLinecap="round" />
                    <path d="M17.5 3.8L16.5 5.5" stroke="url(#vision-grad)" strokeWidth="1.3" strokeLinecap="round" />
                    <path d="M6.5 3.8L7.5 5.5" stroke="url(#vision-grad)" strokeWidth="1.3" strokeLinecap="round" />
                  </svg>
                </IconTile>
                <h3
                  id="vision-title"
                  className="text-[#0B1D3A] font-semibold text-[clamp(18px,2.2vw,20px)] leading-[1.3]"
                >
                  Our Vision
                </h3>
              </div>

              <p className="col-span-12 mt-2 text-[#0B1D3A] text-[clamp(16px,2.1vw,16px)] leading-[clamp(22px,3.2vw,24px)] max-w-[70ch]">
                To become one of India’s most reliable custom label manufacturing companies through strong production systems, modern printing technology and customer focused manufacturing support.
              </p>

              <div className="col-span-12 mt-5">
                <OutlineButton href="/contact" className="text-[#174873]">
                  Start Your Journey
                </OutlineButton>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
}

export default memo(StandForSectionComponent);
